import React, { useEffect, useState } from 'react';
import { makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { SquareFilled } from '@fluentui/react-icons';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import DOMPurify from 'dompurify';
import { customColors, customFonts } from '../../../styles';
import { OutputTable } from './output-components/OutputTable';
import { OutputPreBlock } from './output-components/OutputPreBlock';
import { OutputCodeBlock } from './output-components/OutputCodeBlock';
import * as utils from '../../utils/TextUtils';

interface ChatHistoryTextContentProps {
    content: string;
    isBot: boolean;
    isStreaming?: boolean;
}

const useStyles = makeStyles({
    root: {
        minWidth: 0,
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        lineHeight: '1.5',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        opacity: 0,
        transition: 'opacity 0.3s ease-in',
    },
    visible: {
        opacity: 1,
    },
    streaming: {
        // Each new block-level element (p, ul, pre, etc.) inserted by ReactMarkdown
        // gets a fade-in + slight rise animation when it first enters the DOM.
        // Existing elements that just receive more text do NOT re-trigger.
        '& > *': {
            animationName: {
                from: { opacity: '0', transform: 'translateY(4px)' },
                to: { opacity: '1', transform: 'translateY(0)' },
            },
            animationDuration: '0.25s',
            animationTimingFunction: 'ease-out',
            animationFillMode: 'both',
        },
    },
    markdownRoot: {
        '& p': {
            ...shorthands.margin('0', '0', '8px', '0'),
            fontFamily: customFonts.Lato,
        },
        '& p:last-child': {
            marginBottom: 0,
        },
        '& ul, & ol': {
            ...shorthands.margin('4px', '0', '8px', '20px'),
            fontFamily: customFonts.Lato,
        },
        '& li': {
            ...shorthands.margin('2px', '0', '2px', '0'),
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            ...shorthands.margin('12px', '0', '6px', '0'),
            fontFamily: customFonts.Lato,
            color: customColors.summarizeButtonDarker,
        },
        '& a': {
            color: customColors.summarizeButtonDarker,
            textDecorationLine: 'underline',
        },
        '& blockquote': {
            ...shorthands.margin('8px', '0'),
            ...shorthands.padding('4px', '12px'),
            borderLeftWidth: '3px',
            borderLeftStyle: 'solid',
            borderLeftColor: customColors.botMessageBorder,
            color: 'rgba(0,0,0,0.6)',
        },
    },
    userText: {
        whiteSpace: 'pre-wrap',
    },
    stoppedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        ...shorthands.gap('4px'),
        ...shorthands.margin('10px', '0', '0', '0'),
        ...shorthands.padding('3px', '8px', '3px', '6px'),
        ...shorthands.borderRadius('12px'),
        ...shorthands.border('1px', 'solid', 'rgba(246, 135, 31, 0.25)'),
        backgroundColor: 'rgba(246, 135, 31, 0.07)',
        color: '#888888',
        fontSize: '12px',
        lineHeight: '1.4',
        '& svg': {
            fill: customColors.protivitiOrange,
            opacity: '0.7',
            flexShrink: '0',
        },
    },
});

const STOPPED_MARKER = '\n\n*(stopped)*';

/**
 * Normalises bot markdown so code fences open on their own line.
 */
function formatBotContent(raw: string): string {
    return utils
        .formatBotChatTextContent(raw)
        .replace(/([^`\n])\`\`\`/g, '$1\n```')
        .replace(/``` /g, '``` \n');
}

/**
 * Replaces Azure AI inline citation markers like 【4:0†source】 with sequential [n] indices.
 */
function replaceMarkers(text: string): string {
    const markerMap = new Map<string, string>();
    let counter = 0;
    return text.replace(/【(\d+)(?::\d+)?†[^】]+】/g, (_, n: string) => {
        if (!markerMap.has(n)) markerMap.set(n, String(++counter));
        return `[${markerMap.get(n)}]`;
    });
}

/**
 * HTML-encodes special characters in user messages so they render safely.
 */
function formatUserContent(raw: string): string {
    return utils
        .formatChatTextContent(raw)
        .trim()
        .replace(/[\u00A0-\u9999<>&]/g, function (i: string) {
            return `&#${i.charCodeAt(0)};`;
        });
}

const ChatHistoryTextContent: React.FC<ChatHistoryTextContentProps> = ({ content, isBot, isStreaming }) => {
    const styles = useStyles();
    const [isVisible, setIsVisible] = useState(!!isStreaming);

    useEffect(() => {
        if (isVisible) return;
        const timer = setTimeout(() => setIsVisible(true), 1);
        return () => clearTimeout(timer);
    }, [isVisible]);

    const rootClass = mergeClasses(styles.root, isVisible ? styles.visible : undefined);

    if (!isBot) {
        const sanitizedHTML = DOMPurify.sanitize(formatUserContent(content));
        return (
            <div
                className={mergeClasses(rootClass, styles.userText)}
                dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
            />
        );
    }

    const wasStopped = content.endsWith(STOPPED_MARKER);
    const cleanContent = wasStopped ? content.slice(0, -STOPPED_MARKER.length) : content;
    const processedContent = replaceMarkers(formatBotContent(cleanContent));

    return (
        <div className={mergeClasses(rootClass, styles.markdownRoot, isStreaming ? styles.streaming : undefined)}>
            <ReactMarkdown
                remarkPlugins={[[remarkGfm, { tablePipeAlign: true, tableCellPadding: true }], remarkBreaks]}
                urlTransform={(url) => (url.startsWith('data:image/') ? url : defaultUrlTransform(url))}
                components={{
                    table: ({ ...props }) => <OutputTable {...props} />,
                    pre: ({ ...props }) => <OutputPreBlock {...props} />,
                    code: ({ ...props }) => <OutputCodeBlock {...props} />,
                    a: ({ href, children, ...props }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                            {children}
                        </a>
                    ),
                    img: ({ src, alt, ...props }) => (
                        <img src={src} alt={alt} style={{ maxWidth: '30%', height: 'auto' }} {...props} />
                    ),
                }}
            >
                {processedContent}
            </ReactMarkdown>
            {wasStopped && (
                <div className={styles.stoppedBadge}>
                    <SquareFilled fontSize={11} />
                    Response interrupted
                </div>
            )}
        </div>
    );
};

export default ChatHistoryTextContent;
